import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";

interface LoadingContextValue {
  isLoading: boolean;
  show: () => void;
  hide: () => void;
  withLoading: <T>(promiseFactory: () => Promise<T>) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextValue | undefined>(
  undefined,
);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [counter, setCounter] = useState(0);

  const show = () => setCounter((prev) => prev + 1);
  const hide = () => setCounter((prev) => (prev > 0 ? prev - 1 : 0));

  const withLoading = async <T,>(
    promiseFactory: () => Promise<T>,
  ): Promise<T> => {
    show();
    try {
      return await promiseFactory();
    } finally {
      hide();
    }
  };

  const value = useMemo(
    () => ({ isLoading: counter > 0, show, hide, withLoading }),
    [counter],
  );

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextValue => {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error("useLoading must be used within LoadingProvider");
  }
  return ctx;
};
